import * as React from 'react';
import { IonMenu, IonContent, IonHeader, IonFooter, IonItemDivider } from '@ionic/react';
import { useField, useNestedApps } from 'oasis-os-contentful';
import { FeatureLoader, useAppState } from 'oasis-os-react';
import { SkeletonLoad, Translate, readGlobal } from 'oasis-os-common';
import './style.css';
import { useAuth } from 'oasis-auth';
import { isPlatform } from '@ionic/core';
import GapFiller from './GapFiller';
import { ImageAsset } from './types';

export default function SideNav() {
  const logo = useField<ImageAsset>('logo');
  const footnote = useField<ImageAsset>('footnoteNew');
  const navigation = useNestedApps('navigation');
  const [auth] = useAuth();
  const authenticated = (auth.authenticated && !isPlatform('hybrid')) || isPlatform('hybrid');
  const sideNavFooter = useNestedApps('sideNavFooter');
  const logoAlt = Translate({ id: `general.logoAlt` })?.props?.children as string;
  const [loading, setLoading] = React.useState(true);
  const showSkelton: boolean = readGlobal(
    `$.global.value.functionalConfig.features.skeletonload.enabled`,
  );

  React.useEffect(() => {
    setLoading(false);
  }, []);

  return (
    <IonMenu side="start" contentId="main" type="push" className={`pwa__side-nav`}>
      <GapFiller />
      {authenticated && (
        <>
          <div className="pwa__side-nav__block" />
          <IonHeader className="pwa__side-nav__header">
            {logo && !loading ? (
              <img
                data-testid="pwa-sidenav-logo"
                src={logo.fields.file.url}
                alt={logoAlt}
                height={54}
                width={161}
              />
            ) : (
              logo && showSkelton && loading && <SkeletonLoad animated variant="logo" />
            )}
          </IonHeader>
          <IonContent className="pwa__side-nav__content" data-testid="pwa-sidenav-content">
            {navigation && !loading
              ? navigation.map(({ name, meta, entry }) => {
                  if (name === 'oasisFeatureNavigationDivider') {
                    return (
                      <IonItemDivider
                        key={`${meta.contentId}_${name}`}
                        className="feature-navigation--divider"
                      />
                    );
                  }

                  if (!meta.showDesktop) {
                    return null;
                  }

                  return (
                    <FeatureLoader
                      key={`${meta.contentId}_${name}`}
                      name={name}
                      version={meta.version || meta.appVersion}
                      app={{ name }}
                      id={meta.contentId}
                      initial={entry}
                      sharedModule="./App"
                      {...meta}
                    />
                  );
                })
              : navigation &&
                loading &&
                showSkelton &&
                navigation.map(() => {
                  return <SkeletonLoad animated variant="rect" />;
                })}
          </IonContent>
          <IonFooter className="pwa__side-nav__footer" data-testid="pwa-sidenav-footer">
            {footnote && footnote.fields.file && (
              <img src={footnote.fields.file.url} alt={footnote.fields.title} />
            )}

            {sideNavFooter && !loading
              ? sideNavFooter.map(({ name, meta, entry }) => {
                  return (
                    <FeatureLoader
                      key={`${meta.contentId}_${name}`}
                      name={name}
                      version={meta.version || meta.appVersion}
                      app={{ name }}
                      id={meta.contentId}
                      initial={entry}
                      sharedModule="./App"
                      {...meta}
                    />
                  );
                })
              : sideNavFooter &&
                loading &&
                showSkelton &&
                sideNavFooter.map(() => {
                  return (
                    <>
                      <SkeletonLoad animated variant="text" />
                      <SkeletonLoad animated variant="text" />
                    </>
                  );
                })}
          </IonFooter>
        </>
      )}
    </IonMenu>
  );
}
